// Who lands on the waitlist page, and how that is decided.
//
// A `network` user signed in SUCCESSFULLY. They are not an error and they are
// not a failed login — the exchange minted them a session and answered 200.
// What they have is an account with no access to THIS app yet, which is
// exactly the shape Ted described for the waitlist.
//
// What they must never do is reach Auger code that talks to Apollo. The
// `network` Hasura role can select two columns of its own `users` row and
// nothing else, so the boot query for email/name/admin/partner/record fails;
// on 2026-09-15 that produced "users_by_pk not found in type 'query_root'"
// and the app hung on "Signing you in" with nothing on screen to explain it.
// The hang is not a timeout — currentUser catches the Apollo error and
// logs it, so `userStore.email` is never set and `waitForLogin()` simply never
// resolves. That is the concrete failure this page fixes.
//
// The decision is made on ROLES, never by catching that Apollo error: the
// error string is not a contract, and by the time it is thrown the stores
// have already started booting. Roles are the audience roles Stytch holds
// (x-hasura-allowed-roles); a Heavybit staff role is what opens this app. The
// `x-hasura-user-class` claim is not read: it comes from users.class and goes
// away with the users table.

import { isHeavybitStaff, isRole } from "@auger/identity"

export const WAITLIST_PATH = "/identity/waitlist"

/**
 * A successful sign-in with no access to THIS app yet: a caller who holds no
 * Heavybit staff role. Staff are never sent here. A caller with no roles
 * (a token the page cannot read) routes normally, because Hasura decides
 * access anyway, and a staff member must never be stranded here. A Network
 * member always holds network_member: the exchange assigns it.
 */
export function isWaitlisted(roles: readonly string[] | null | undefined): boolean {
  if (!roles) return false
  if (isHeavybitStaff({ roles })) return false
  return roles.length > 0
}

/**
 * WHICH message the waitlist page shows. Two people reach that page and they
 * are not in the same situation:
 *
 *   `waiting`          signed in, holds no audience role yet. Genuinely on the
 *                      list.
 *   `no_auger_access`  holds an audience role for another Heavybit product
 *                      (network_member: Crucible) and opened the wrong
 *                      application. Not on any list, and telling them they are
 *                      would be false.
 *
 * Returns null for anybody `isWaitlisted` would not route here, so the two can
 * never disagree about who belongs on the page. The gate stays the gate; this
 * only chooses words.
 */
export type WaitlistState = "waiting" | "no_auger_access"

export function waitlistState(roles: readonly string[] | null | undefined): WaitlistState | null {
  if (!roles || !isWaitlisted(roles)) return null
  return roles.some(isRole) ? "no_auger_access" : "waiting"
}
